import React from 'react';
import { Link } from '@mui/material';
import { camelToTitle } from 'utils';

export const formOrderObjFromOrderDetails = (catalogServiceId = null, data = null) => {
    const entityDetails = data?.entityDetails || null;
    const catalog = data?.catalogServices?.find((item) => item?.catalogServiceId === catalogServiceId) || null;
    const catalogConfig = catalog?.catalogConfig || null;
    const hasContributorOwner = catalogConfig?.createContributorAdGroup !== undefined;
    const hasReaderOwner = catalogConfig?.createReaderAdGroup !== undefined;
    const hasContributorGroups = Boolean(catalogConfig?.contributorAdGroups?.length);
    const hasReaderGroups = Boolean(catalogConfig?.readerAdGroups?.length);

    // Set privilegedADGroup value based on conditions
    let privilegedADGroup = null;
    if (hasContributorGroups) {
        privilegedADGroup = 'Yes';
    } else if (hasContributorOwner) {
        privilegedADGroup = catalogConfig?.createContributorAdGroup ? 'No' : '';
    } else if (catalogConfig?.typeOfCloud?.toUpperCase() === 'AWS' && catalogConfig?.activeDirectoryGroupOwnerId) {
        privilegedADGroup = 'No';
    }

    // Set nonPrivilegedADGroup value based on conditions
    let nonPrivilegedADGroup = null;
    if (hasReaderGroups) {
        nonPrivilegedADGroup = 'Yes';
    } else if (hasReaderOwner) {
        nonPrivilegedADGroup = catalogConfig?.createReaderAdGroup ? 'No' : '';
    }
    return {
        ...(data?.id && { id: data?.id }),
        ...(catalogConfig?.onboardingCategory === 'Sandbox' &&
            entityDetails?.name && {
                appName: entityDetails?.name || null,
            }),
        entityDetails: {
            ...entityDetails,
            owner: { name: entityDetails?.applicationManager, emailId: entityDetails?.applicationManagerEmail, id: entityDetails?.applicationManagerGpid },
            financialDetails: catalogConfig?.budgetCode,
            sector: catalogConfig?.sector,
        },
        catalogCode: catalog?.catalogServiceCode,
        catalogType: catalog?.catalogType,
        cloudProvider: catalog?.cloudProvider,
        cloudService: catalog?.cloudService,

        // Application Section
        appOwner: `${entityDetails?.applicationManagerGpid} - ${entityDetails?.applicationManager}`,
        appOwnerDetails: {
            label: `${entityDetails?.applicationManagerGpid} - ${entityDetails?.applicationManager}`,
            gpid: entityDetails?.applicationManagerGpid,
            name: entityDetails?.applicationManager,
            email: entityDetails?.applicationManagerEmail,
        },
        businessApp: catalogConfig?.businessApplication || null,
        appProfile: catalogConfig?.applicationService || null,
        ...(catalogConfig?.applicationService && { appProfileDetails: { label: catalogConfig?.applicationService, value: catalogConfig?.applicationService } }),
        ...(!catalogConfig?.applicationService && { appProfileDetails: null }),
        sector: catalogConfig?.sector || null,
        region: catalogConfig?.region || null,
        regionDisplayname: catalogConfig?.region,
        businessSponsor: catalogConfig?.businessSponsor?.name || null,
        businessSponsorDetails: null,
        environments: catalogConfig?.environment || null,
        applicationName: catalogConfig?.applicationName || null,
        appShortName: catalogConfig?.applicationShortName || null,
        awsAccountName: catalogConfig?.awsAccountName || null,
        department: catalogConfig?.department || null,
        appRegion: catalogConfig?.applicationRegion || null,
        awsSector: catalogConfig?.sector || null,
        awsEnvironment:
            catalogConfig?.environment && Array.isArray(catalogConfig.environment) && catalogConfig.environment.length > 0
                ? catalogConfig.environment[0]
                : null,
        awsRegion: catalogConfig?.region || null,
        awsOrgUnit: catalogConfig?.organizationalUnit || null,
        businessCategory: catalogConfig?.businessCategory || null,
        budgetCode: catalogConfig?.costCenter || null,
        isValidBudgetCode: Boolean(catalogConfig?.costCenter),
        enabledManualBudgetCode: catalogConfig?.enabledManualBudgetCode || true,
        csaApproval: catalogConfig?.csaApproval || null,
        isCSAApprovalsComplete: catalogConfig?.cloudTechDocLink ? 'yes' : 'no',
        techDocLink: catalogConfig?.cloudTechDocLink || null,
        onPremHCode: catalogConfig?.onPremCostCenter || null,
        onPremHCodeDetails: { label: catalogConfig?.onPremCostCenter, value: catalogConfig?.onPremCostCenter } || null,
        additionalInfo: catalogConfig?.additionalInformation || null,
        dataClassification: catalogConfig?.dataClassification,

        // Resource Section
        cloudType: catalogConfig?.typeOfCloud?.toUpperCase() || null,
        onboardingCategory: catalogConfig?.onboardingCategory || null,
        isaNumber: catalogConfig?.isaNumber || null,
        ...(catalogConfig?.tacRequest && {
            tacRequest: catalogConfig?.tacRequest || null,
        }),
        prevCloudOnboardReq: catalogConfig?.previousCloudOnboardingRequest || null,
        adGroup: catalogConfig?.adGroup?.name || null,
        questionnaire: catalogConfig?.questionnaireResponses || {},
        // Only include AD group values if they are not null
        ...(privilegedADGroup !== null && { privilegedADGroup }),
        ...(nonPrivilegedADGroup !== null && { nonPrivilegedADGroup }),
        ...(privilegedADGroup === 'Yes' && {
            privilegedADGroupList:
                catalogConfig?.contributorAdGroups?.map((app) => ({
                    contributorValue: app?.name,
                    contributorInputValue: app?.name,
                })) || [],
        }),
        ...(nonPrivilegedADGroup === 'Yes' && {
            nonPrivilegedADGroupList:
                catalogConfig?.readerAdGroups?.map((app) => ({
                    readerValue: app?.name,
                    readerInputValue: app?.name,
                })) || [],
        }),
        ...(nonPrivilegedADGroup === 'No' && {
            nonPrivilegedADGroupOwner: catalogConfig?.activeDirectoryGroupOwnerId,
            nonPrivilegedADGroupOwnerDetails: null,
            applicationName: catalogConfig?.applicationName || null,
            appShortName: catalogConfig?.applicationShortName || null,
        }),
        ...(privilegedADGroup === 'No' && {
            privilegedADGroupOwner: catalogConfig?.activeDirectoryGroupOwnerId,
            privilegedADGroupOwnerDetails: null,
            applicationName1: catalogConfig?.applicationName || null,
            appShortName1: catalogConfig?.applicationShortName || null,
        }),
        ...(catalogConfig?.typeOfCloud?.toUpperCase() === 'AWS' && {
            privilegedADGroupOwner: catalogConfig?.activeDirectoryGroupOwnerId,
            privilegedADGroupOwnerDetails: null,
        }),
        isaValidationDetails: null,
        validationStatus: 'clearValidation',
        isTouched: false,
    };
};

export const formUploadObjFromOrderDetails = (catalogServiceId = null, data = null) => {
    const catalog = data?.catalogServices?.find((item) => item?.catalogServiceId === catalogServiceId) || null;
    const catalogConfig = catalog?.catalogConfig || null;

    return (
        catalogConfig?.attachments?.map((item) => ({
            id: item?.id,
            name: item?.fileName,
            blobRelativeLink: item?.blobRelativeLink,
            size: item?.fileSize || 0,
        })) || []
    );
};

export const resetOrder = {
    entityDetails: null,
    billingEntity: null,
    appProfile: null,
    appProfileDetails: null,
    appName: null,
    applicationName: null,
    appShortName: null,
    appOwner: null,
    appOwnerDetails: null,
    sector: null,
    sectorDetails: null,
    region: null,
    regionDetail: null,
    environments: [],
    awsEnvironment: null,
    awsAccountName: null,
    awsOrgUnit: null,
    awsOrgUnitDetails: null,
    businessCategory: null,
    budgetCode: null,
    isValidBudgetCode: false,
    enabledManualBudgetCode: false,
    csaApproval: null,
    techDocLink: null,
    isCSAApprovalsComplete: null,
    onPremHCode: null,
    onPremHCodeDetails: null,
    additionalInfo: null,
    businessAppDetails: null,
    businessApp: null,
    businessSponsor: null,
    businessSponsorDetails: null,
    dataClassification: null,

    // Resource Setion
    cloudType: null,
    isaNumber: null,
    onboardingCategory: null,
    // csaApprovedArch: 'no',
    tacRequest: null,
    prevCloudOnboardReq: null,
    csa: null,
    csaDetails: null,
    privilegedADGroupList: [{ contributorValue: '' }],
    privilegedADGroup: null,
    privilegedADGroupOwner: null,
    privilegedADGroupOwnerDetails: null,
    deletedFiles: [],
    isChinaRegion: 'no',
    adGroup: null,
    questionnaire: {},
    estimatedCost: null,
    placeNewOrder: new Date(),
    validationStatus: 'clearValidation',
    isTouched: false,
};

export const resetCostEstimateDetails = {
    totalRecurringCost: {
        frequency: 'monthly',
        estCost: 0,
        currency: 'USD',
    },
    totalOneTimeCost: {
        frequency: 'One Time',
        estCost: 0,
        currency: 'USD',
    },

    notes: 'There is no cost for this Cloud onboarding request, however there will be cost involved based on the service Name defined at the Resource Group level for your resources that being provisioned in future.',
};

export const resetToggleSspModal = {
    isCost: { costType: '', isCostOpen: false },
    isTagOpen: false,
    isApplicationOpen: false,
    isSubmitOrderSuccess: false,
    isUpdatedToCart: false,
    isCostSplitOpen: false,
    isNonPrivilegedADGroupOpen: false,
    isPrivilegedADGroupOpen: false,
    isADGroupModalOpen: false,
};
export const isUniqueOwner = (value = null, selectedvalues = []) => {
    return selectedvalues.indexOf(value) === -1;
};
export const getValueFromKey = (searchKey, keyValuePairs) => {
    return keyValuePairs[searchKey] ? keyValuePairs[searchKey] : camelToTitle(searchKey);
};
export const mappingLabel = {
    isaNumber: 'ISA Number',
    isaStatus: 'ISA Status',
    parentIsaNumber: 'Parent ISA Number',
    parentIsaNumberStatus: 'Parent ISA Number Status',
    scanNumber: 'SCAN Number',
    scanStatus: 'SCAN Status',
    scanTaskNumber: 'SCAN Task Number',
    scanTaskState: 'SCAN Task State',
};

export const certificateExist = (appUrl) => {
    const url = /\.com$/.test(appUrl);
    if (url) {
        return !/\.azure\.intra(?:\.pepsico\.com|\.mypepsico\.com)$/.test(appUrl);
    }
    return false;
};

export const isDuplicate = (adGroupArray, adGroup) => {
    const duplicateReaderADGroups = adGroupArray?.filter((item, index) => adGroupArray.indexOf(item) !== index);
    if (adGroup) {
        return duplicateReaderADGroups.indexOf(adGroup) > -1;
    }
    return false;
};

export const isADGroupError = (validationMessage = [], adGroup = null) => {
    let errorCount = 0;
    let adGroupError = false;
    if (Array.isArray(validationMessage)) {
        validationMessage?.forEach((item) => {
            if (item.includes(adGroup)) {
                // eslint-disable-next-line no-plusplus
                errorCount++;
            }
        });
    }
    adGroupError = errorCount !== 0;
    return adGroupError;
};

export const isADGroupFormValid = (order) => {
    let errorCount = 0;
    let isValid = false;
    if (order?.adGroupList?.length) {
        order?.adGroupList?.forEach((item) => {
            if (item && (item?.adGroupValue === '' || item?.adGroupValue === null)) {
                errorCount += 1;
            }
        });
    }
    isValid = errorCount === 0;
    return isValid;
};

export const isADGroupDuplicate = (order) => {
    const adGroupArray = order?.adGroupList?.map((item) => item?.adGroupValue);
    return new Set(adGroupArray).size !== adGroupArray.length;
};

export const questions = [
    {
        question: 'Do you need assistance with provisioning cloud resources?',
        type: 'checkbox',
        options: [
            {
                label: (
                    <>
                        Yes -{' '}
                        <Link href="https://devx-infra.mypepsico.com/services" target="_blank" rel="noopener noreferrer">
                            DevX-Infra
                        </Link>{' '}
                        (e.g: VM, Database, External/Internal URL Onboarding, SSO Onboarding, AKS Namespace, Virtual Service, Private Endpoint, Load Balancer,
                        Kafka Topic, SQL PaaS Database, Storage Account.)
                    </>
                ),
                value: 'Yes - DevX-Infra (e.g: VM, Database, External/Internal URL Onboarding, SSO Onboarding, AKS Namespace, Virtual Service, Private Endpoint, Load Balancer, Kafka Topic, SQL PaaS Database, Storage Account.)',
            },
            {
                label: (
                    <>
                        Yes -{' '}
                        <Link
                            href="https://pepsico.service-now.com/now/nav/ui/classic/params/target/com.glideapp.servicecatalog_cat_item_view.do%3Fv%3D1%26sysparm_id%3D04974f25479cd210bb2ef564116d43c1%26sysparm_link_parent%3Ddf774ce50f511700fa1b1b1e51050e98%26sysparm_catalog%3De0d08b13c3330100c8b837659bba8fb4%26sysparm_catalog_view%3Dcatalog_default%26sysparm_view%3Dcatalog_default"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Others
                        </Link>{' '}
                        (e.g: NSG, Databricks, etc)
                    </>
                ),
                value: 'Yes - Others (e.g: NSG, Databricks, etc)',
            },
            { label: 'No', value: 'No' },
        ],
    },
    {
        question: 'Do you need help for raising an ISA?',
        type: 'radio',
        options: [
            {
                label: (
                    <>
                        Yes -{' '}
                        <Link
                            href="https://pepsico.service-now.com/now/nav/ui/classic/params/target/x_inpe_information_isa.do"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Create New ISA Request
                        </Link>
                    </>
                ),
                value: 'Yes',
            },
            { label: 'No', value: 'No' },
        ],
    },
    {
        question: 'Do you need guidance through the NSG intake process?',
        type: 'radio',
        options: [
            {
                label: (
                    <>
                        Yes -{' '}
                        <Link
                            href="https://pepsico.sharepoint.com/:v:/r/teams/GlobalCloudPlatformEnablementPursuit/Shared%20Documents/General/NSG%20Agenda%20Review/CPE%20NSG%20Form.mp4?csf=1&web=1&e=fXfXNm&nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZy1MaW5rIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXcifX0%3D"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Watch Video Reel
                        </Link>
                    </>
                ),
                value: 'Yes - Watch Video Reel',
            },
            {
                label: (
                    <>
                        No -{' '}
                        <Link
                            href="https://pepsico.service-now.com/now/nav/ui/classic/params/target/com.glideapp.servicecatalog_cat_item_view.do%3Fv%3D1%26sysparm_id%3D04974f25479cd210bb2ef564116d43c1%26sysparm_link_parent%3Ddf774ce50f511700fa1b1b1e51050e98%26sysparm_catalog%3De0d08b13c3330100c8b837659bba8fb4%26sysparm_catalog_view%3Dcatalog_default%26sysparm_view%3Dcatalog_default"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Only KB article
                        </Link>
                    </>
                ),
                value: 'No - Only KB article',
            },
        ],
    },
    {
        question: 'Does your app need to talk to anything outside cloud—for example: PEP on-prem systems or other internet endpoints (Firewall support)?',
        type: 'radio',
        options: [
            { label: 'Yes', value: 'Yes' },
            { label: 'No', value: 'No' },
        ],
    },
];
